import { useCallback } from 'react';
import { Play } from 'lucide-react';

import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import {
    useArticlePhotoViewer,
    type OpenPhotoViewerOptions,
} from './ArticlePhotoViewer';

/**
 * Set on the banner's still when the editor gave the article a video. It is
 * both how the viewer knows to build that slide a player and how the control
 * below finds the picture to open.
 */
export const ARTICLE_BANNER_VIDEO_ATTR = 'data-article-photo-video';

/**
 * Opens the banner's film in the article's viewer.
 *
 * The banner is found in the document rather than handed down: the control
 * that plays it sits in the masthead's own headline stack, several components
 * away from the picture, and the viewer already reads the page this way.
 */
export const useBannerVideo = () => {
    const openPhotoViewer = useArticlePhotoViewer();
    const tracker = useTracker();

    const open = useCallback(
        (options?: OpenPhotoViewerOptions) => {
            const banner = document.querySelector<HTMLElement>(
                `[${ARTICLE_BANNER_VIDEO_ATTR}]`,
            );
            if (!banner || !openPhotoViewer) return;
            // Once per banner, not once per opening — the flag rides the element,
            // so it goes when the article does and a reader who closes the viewer
            // and opens it again is still one view of one film.
            if (!banner.dataset.videoLogged) {
                banner.dataset.videoLogged = '1';
                tracker.log('Video', { url: new URL(window.location.href) });
            }
            openPhotoViewer(banner, options);
        },
        [openPhotoViewer, tracker],
    );

    return { canOpen: Boolean(openPhotoViewer), open };
};

/**
 * The masthead's play control, on the headline's own gutter under the byline.
 *
 * It rides the headline stack rather than the picture: the banner's box is
 * sized from the viewport's width and the headline's from its top, so a
 * control centred in the picture drifts away from the words as the window
 * widens. Ranged left under Back, it stays in the column it belongs to.
 */
export const ArticleBannerPlayButton = () => {
    const { canOpen, open } = useBannerVideo();
    if (!canOpen) return null;

    return (
        <div className="w-full px-4.5 pt-6 md:px-[3rem]">
            <button
                type="button"
                onClick={() => open()}
                className={cn(
                    detailCta('white-lifted'),
                    'pointer-events-auto gap-[0.5rem] max-md:h-11 max-md:rounded-[var(--radius-btn-m)] max-md:px-4 max-md:text-sm',
                )}
            >
                <Play
                    aria-hidden="true"
                    fill="currentColor"
                    strokeWidth={0}
                    className="size-[1.125rem] shrink-0 translate-x-[1px]"
                />
                Play video
            </button>
        </div>
    );
};
